import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router';
import type { Spot, LikeStatusResponse, SpotCategory, CheckinResponse } from '@ridehub/shared';
import { getCategoryLabel, CATEGORY_MAP, calculateStarScore } from '@ridehub/shared';
import { StarRating } from '../../components/StarRating.js';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { Button } from '../../components/Button.js';
import { CheckinButton } from './CheckinButton.js';
import { CommentSection } from './CommentSection.js';
import { RecentCheckins } from './RecentCheckins.js';
import { PhotoUpload } from './PhotoUpload.js';
import { ReportModal } from '../../components/ReportModal.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { SpotWeatherSection } from './SpotWeatherSection.js';

type Tab = 'photos' | 'checkins' | 'comments';

export function SpotDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, dbUser } = useAuth();

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/map');
    }
  };

  const [spot, setSpot] = useState<(Spot & { user: { id: string; nickname: string } }) | null>(
    null
  );
  const [likeStatus, setLikeStatus] = useState<LikeStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingPhotoId, setReportingPhotoId] = useState<string | null>(null);
  const [localCheckinCount, setLocalCheckinCount] = useState<number | null>(null);
  const [checkinRefreshKey, setCheckinRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const param = new URLSearchParams(location.search).get('tab');
    if (param === 'comments' || param === 'checkins') return param;
    return 'photos';
  });
  const [commentCount, setCommentCount] = useState(0);
  const [initialComments, setInitialComments] = useState<unknown[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [loginHintVisible, setLoginHintVisible] = useState(false);
  const loginHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [photoDeleteTarget, setPhotoDeleteTarget] = useState<string | null>(null);
  const [spotDeleteConfirm, setSpotDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiClient.get<Spot>(`/spots/${id}`),
      user
        ? apiClient.get<LikeStatusResponse>(`/likes/status/${id}`, { requireAuth: true })
        : Promise.resolve(null),
      apiClient.get<unknown[]>(`/spots/${id}/comments`),
    ]).then(([spotData, likeData, commentsData]) => {
      if (spotData) {
        setSpot(spotData as Spot & { user: { id: string; nickname: string } });
        setIsBookmarked(!!(spotData as { isBookmarked?: boolean }).isBookmarked);
      }
      if (likeData) setLikeStatus(likeData);
      const loaded = commentsData ?? [];
      setInitialComments(loaded);
      setCommentCount(loaded.length);
      setLoading(false);
    });
  }, [id, user]);

  const handleUnauthLikeClick = () => {
    if (loginHintTimerRef.current) clearTimeout(loginHintTimerRef.current);
    setLoginHintVisible(true);
    loginHintTimerRef.current = setTimeout(() => setLoginHintVisible(false), 3000);
  };

  const handleLikeToggle = async () => {
    if (!user || !id) return;
    setLikeLoading(true);
    try {
      const result = await apiClient.post<LikeStatusResponse>(
        '/likes/toggle',
        { spotId: id },
        { requireAuth: true }
      );
      if (result) setLikeStatus(result);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleBookmarkToggle = async () => {
    if (!dbUser || !id) return;
    const prev = isBookmarked;
    setIsBookmarked(!prev);
    try {
      if (prev) {
        await apiClient.delete(`/bookmarks/${id}`, { requireAuth: true });
      } else {
        await apiClient.post(`/bookmarks/${id}`, {}, { requireAuth: true });
      }
    } catch {
      setIsBookmarked(prev);
    }
  };

  const handleCheckinSuccess = (_result: CheckinResponse) => {
    setLocalCheckinCount((prev) => (prev ?? spot?.totalCheckins ?? 0) + 1);
    setCheckinRefreshKey((k) => k + 1);
  };

  const handleCommentCountChange = useCallback((count: number) => {
    setCommentCount(count);
  }, []);

  const isOwner = !!dbUser && !!spot && spot.userId === dbUser.id;

  const handleShare = async () => {
    const url = window.location.href;
    const title = spot?.name ?? 'RideHub スポット';
    const text = spot
      ? `RideHub のスポット「${spot.name}」\n★${calculateStarScore(spot.rankScore).toFixed(1)} · チェックイン ${spot.totalCheckins}回`
      : 'RideHub のスポット';
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // ユーザーキャンセルは無視
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch {
        // クリップボードが使えない場合は何もしない
      }
    }
  };

  const handlePhotoDelete = async (photoId: string) => {
    try {
      await apiClient.delete(`/photos/${photoId}`, { requireAuth: true });
      let newLength = 0;
      setSpot((prev) => {
        if (!prev) return prev;
        const photos = prev.photos.filter((p) => p.id !== photoId);
        newLength = photos.length;
        const defaultPhotoUrl =
          prev.defaultPhotoUrl === prev.photos.find((p) => p.id === photoId)?.url
            ? undefined
            : prev.defaultPhotoUrl;
        return { ...prev, photoUrls: photos.map((p) => p.url), photos, defaultPhotoUrl };
      });
      setLightboxIndex((prev) => {
        if (prev === null) return null;
        if (newLength === 0) return null;
        return Math.min(prev, newLength - 1);
      });
    } catch {
      setOperationError('写真の削除に失敗しました。もう一度お試しください。');
    } finally {
      setPhotoDeleteTarget(null);
    }
  };

  const handleSetDefaultPhoto = async (photoId: string, photoUrl: string) => {
    try {
      await apiClient.put(`/photos/${photoId}/set-default`, {}, { requireAuth: true });
      setSpot((prev) => (prev ? { ...prev, defaultPhotoUrl: photoUrl } : prev));
    } catch {
      setOperationError('代表写真の設定に失敗しました。もう一度お試しください。');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/spots/${id}`, { requireAuth: true });
      navigate('/spots');
    } catch {
      setOperationError('削除に失敗しました。もう一度お試しください。');
    } finally {
      setDeleteLoading(false);
      setSpotDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!spot) {
    return <p className="text-center py-8 text-gray-500">スポットが見つかりません</p>;
  }

  const categoryDef = CATEGORY_MAP.get(spot.category as SpotCategory);
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${spot.latitude},${spot.longitude}&travelmode=bicycling`;
  const displayCheckinCount = localCheckinCount ?? spot.totalCheckins;

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'photos', label: '写真', count: spot.photoUrls.length },
    { key: 'checkins', label: 'チェックイン', count: displayCheckinCount },
    { key: 'comments', label: 'コメント', count: commentCount },
  ];

  return (
    <>
      {/* 固定ヘッダー（ウィンドウスクロールでも常に最上部に表示） */}
      <div className="fixed top-0 inset-x-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-2xl mx-auto flex items-center gap-2 px-2 py-2">
          <button
            onClick={handleBack}
            className="p-2 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 flex-shrink-0"
            aria-label="戻る"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5M12 5l-7 7 7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2 className="flex-1 text-sm font-semibold text-gray-900 truncate">{spot.name}</h2>
        </div>
      </div>

      {/* 固定ヘッダーの高さ分（52px）を余白として確保 */}
      <div className="max-w-2xl mx-auto pt-[52px]">
        <div className="p-4">
          {/* ヘッダー: アイコン（下にカテゴリ名）＋ スポット名・住所・登録者 */}
          <div className="flex items-start gap-3 mb-4">
            <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-0.5">
              <span
                className="text-4xl"
                role="img"
                aria-label={getCategoryLabel(spot.category as SpotCategory)}
              >
                {categoryDef?.icon ?? '📍'}
              </span>
              <span className="text-xs text-gray-500 text-center leading-tight max-w-[56px]">
                {getCategoryLabel(spot.category as SpotCategory)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold leading-snug">{spot.name}</h1>
              {spot.address && <p className="text-sm text-gray-500 mt-0.5">{spot.address}</p>}
              <p className="text-xs text-gray-400 mt-1">
                登録者:{' '}
                <Link to={`/users/${spot.userId}`} className="text-primary hover:underline">
                  {(spot as Spot & { user: { nickname: string } }).user?.nickname ?? '不明'}
                </Link>
                {' · '}
                {new Date(spot.createdAt).toLocaleDateString('ja-JP')} 登録
              </p>
            </div>
          </div>

          {/* 統計: スポットランク・チェックイン・いいね */}
          <div className="flex divide-x divide-gray-100 border-y border-gray-100 mb-4 -mx-4">
            <div className="flex-1 text-center px-2 py-3">
              <div className="flex justify-center mb-1">
                <StarRating score={calculateStarScore(spot.rankScore)} />
              </div>
              <div className="text-xs text-gray-400">スポットランク</div>
            </div>
            <div className="flex-1 text-center px-2 py-3">
              <div className="font-bold text-base">{displayCheckinCount}</div>
              <div className="text-xs text-gray-400 mt-1">チェックイン</div>
            </div>
            <div className="flex-1 text-center px-2 py-3">
              <div className="font-bold text-base">{likeStatus?.totalLikes ?? spot.totalLikes}</div>
              <div className="text-xs text-gray-400 mt-1">いいね</div>
            </div>
          </div>

          {/* 説明 */}
          {spot.description && (
            <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">{spot.description}</p>
          )}

          {/* タグ */}
          {spot.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {spot.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 border border-gray-200 text-gray-400 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* 天気 */}
          {spot.latitude && spot.longitude && (
            <SpotWeatherSection lat={spot.latitude} lng={spot.longitude} />
          )}

          {/* アクションボタン */}
          <div className="flex flex-col gap-2 mb-4">
            {id && <CheckinButton spotId={id} onCheckinSuccess={handleCheckinSuccess} />}
            {dbUser ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={likeStatus?.liked ? 'primary' : 'secondary'}
                  size="md"
                  className={likeStatus?.liked ? '' : 'border border-gray-300'}
                  loading={likeLoading}
                  onClick={handleLikeToggle}
                >
                  {likeStatus?.liked ? '❤️ いいね済' : '🤍 いいね'}
                </Button>
                <button
                  type="button"
                  onClick={() => void handleBookmarkToggle()}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                    isBookmarked
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔖 ブックマーク
                </button>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  🗺️ ナビ
                </a>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  {shareCopied ? '✅ コピー済み' : '🔗 シェア'}
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant={likeStatus?.liked ? 'primary' : 'secondary'}
                  size="md"
                  className={`flex-1${!likeStatus?.liked ? ' border border-gray-300' : ''}`}
                  loading={likeLoading}
                  onClick={handleUnauthLikeClick}
                >
                  {likeStatus?.liked ? '❤️ いいね済' : '🤍 いいね'}
                </Button>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  🗺️ ナビ
                </a>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  {shareCopied ? '✅ コピー済み' : '🔗 シェア'}
                </button>
              </div>
            )}
          </div>

          {/* 編集・削除ボタン（登録者のみ表示） */}
          {isOwner && (
            <div className="flex gap-2 mb-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                onClick={() => navigate(`/spots/${id}/edit`)}
              >
                ✏️ スポットを編集
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                loading={deleteLoading}
                onClick={() => setSpotDeleteConfirm(true)}
              >
                🗑️ スポットを削除
              </Button>
            </div>
          )}

          {/* 通報ボタン（ログイン済み・登録者以外） */}
          {user && !isOwner && (
            <div className="mb-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                onClick={() => setReportModalOpen(true)}
              >
                🚩 通報する
              </Button>
            </div>
          )}
        </div>

        {/* タブバー */}
        <div className="border-b border-gray-200 px-4">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.key
                      ? 'bg-primary/10 text-primary'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* タブコンテンツ */}
        <div className="p-4">
          {activeTab === 'photos' && (
            <div>
              {user && id && (
                <div className="mb-4">
                  <PhotoUpload
                    spotId={id}
                    onUploadSuccess={(newPhotos) => {
                      setSpot((prev) =>
                        prev
                          ? {
                              ...prev,
                              photoUrls: [...prev.photoUrls, ...newPhotos.map((p) => p.url)],
                              photos: [...prev.photos, ...newPhotos],
                            }
                          : prev
                      );
                    }}
                  />
                </div>
              )}
              {spot.photoUrls.length > 0 ? (
                <div className="grid grid-cols-3 gap-1">
                  {spot.photos.map((photo, index) => {
                    const isDefault = spot.defaultPhotoUrl === photo.url;
                    return (
                      <div key={photo.id} className="relative">
                        <img
                          src={photo.url}
                          alt=""
                          className="w-full aspect-square object-cover rounded cursor-pointer"
                          onClick={() => setLightboxIndex(index)}
                        />
                        {/* 代表写真バッジ（常時表示） */}
                        {isDefault && (
                          <span className="absolute top-1 left-1 text-yellow-400 text-lg pointer-events-none drop-shadow">
                            ★
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">写真はまだありません</p>
              )}
            </div>
          )}

          {activeTab === 'checkins' && id && <RecentCheckins key={checkinRefreshKey} spotId={id} />}

          {activeTab === 'comments' && id && (
            <CommentSection
              spotId={id}
              initialComments={initialComments}
              onCountChange={handleCommentCountChange}
              targetCommentId={new URLSearchParams(location.search).get('commentId') ?? undefined}
            />
          )}
        </div>

        {/* ライトボックス */}
        {lightboxIndex !== null && (
          <div
            className="fixed inset-0 z-[3000] bg-black flex items-center justify-center"
            onClick={() => setLightboxIndex(null)}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchStartX.current === null) return;
              const diff = touchStartX.current - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 50) {
                setLightboxIndex((prev) => {
                  if (prev === null) return null;
                  if (diff > 0) return Math.min(prev + 1, spot.photos.length - 1);
                  return Math.max(prev - 1, 0);
                });
              }
              touchStartX.current = null;
            }}
          >
            <img
              src={spot.photos[lightboxIndex].url}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {/* 閉じるボタン */}
            <button
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-9 h-9 flex items-center justify-center text-lg"
              onClick={() => setLightboxIndex(null)}
            >
              ✕
            </button>
            {/* 前の写真 */}
            {lightboxIndex > 0 && (
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full w-9 h-9 flex items-center justify-center text-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(lightboxIndex - 1);
                }}
              >
                ‹
              </button>
            )}
            {/* 次の写真 */}
            {lightboxIndex < spot.photos.length - 1 && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full w-9 h-9 flex items-center justify-center text-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(lightboxIndex + 1);
                }}
              >
                ›
              </button>
            )}
            {/* 枚数インジケーター */}
            <div className="absolute bottom-24 left-0 right-0 flex justify-center gap-1.5">
              {spot.photos.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === lightboxIndex ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
            {/* アクションバー */}
            {(() => {
              const lbPhoto = spot.photos[lightboxIndex];
              const lbIsDefault = spot.defaultPhotoUrl === lbPhoto?.url;
              if (!user || !lbPhoto) return null;
              return (
                <div
                  className="absolute bottom-0 left-0 right-0 flex justify-around px-4 py-3 bg-black/60"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isOwner && (
                    <button
                      onClick={() => !lbIsDefault && handleSetDefaultPhoto(lbPhoto.id, lbPhoto.url)}
                      className={`flex flex-col items-center gap-1 text-xs px-8 py-2 ${lbIsDefault ? 'text-yellow-400 cursor-default' : 'text-white'}`}
                    >
                      <span className="text-2xl">{lbIsDefault ? '★' : '☆'}</span>
                      メイン
                    </button>
                  )}
                  {dbUser?.id === lbPhoto.userId && (
                    <button
                      onClick={() => setPhotoDeleteTarget(lbPhoto.id)}
                      className="flex flex-col items-center gap-1 text-white text-xs px-8 py-2"
                    >
                      <span className="text-xl">🗑️</span>
                      削除
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setLightboxIndex(null);
                      setReportingPhotoId(lbPhoto.id);
                    }}
                    className="flex flex-col items-center gap-1 text-white text-xs px-8 py-2"
                  >
                    <span className="text-xl">🚩</span>
                    通報
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* スポット通報モーダル */}
        {reportModalOpen && id && (
          <ReportModal targetType="spot" targetId={id} onClose={() => setReportModalOpen(false)} />
        )}

        {/* 写真通報モーダル */}
        {reportingPhotoId && (
          <ReportModal
            targetType="photo"
            targetId={reportingPhotoId}
            onClose={() => setReportingPhotoId(null)}
          />
        )}

        {/* 写真削除確認ダイアログ */}
        {photoDeleteTarget && (
          <ConfirmDialog
            title="写真を削除"
            message="この操作は取り消せません。"
            onConfirm={() => void handlePhotoDelete(photoDeleteTarget)}
            onCancel={() => setPhotoDeleteTarget(null)}
          />
        )}

        {/* スポット削除確認ダイアログ */}
        {spotDeleteConfirm && (
          <ConfirmDialog
            title="スポットを削除"
            message={'このスポットを削除しますか？\nこの操作は取り消せません。'}
            onConfirm={() => void handleDelete()}
            onCancel={() => setSpotDeleteConfirm(false)}
          />
        )}

        {/* 未ログイン時いいね誘導トースト */}
        {loginHintVisible && (
          <div
            className="fixed left-4 right-4 z-[3000] flex items-center justify-between bg-gray-900 text-white rounded-xl px-4 py-3 shadow-lg"
            style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px) + 8px)' }}
          >
            <span className="text-sm">ログインするといいねできます</span>
            <Link
              to="/login"
              className="text-sm font-medium text-primary-300 ml-3 whitespace-nowrap"
            >
              ログイン →
            </Link>
          </div>
        )}

        {/* 操作エラーバナー */}
        {operationError && (
          <div
            className="fixed left-4 right-4 z-[3000] bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex justify-between items-center shadow-md"
            style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px) + 8px)' }}
          >
            <p className="text-sm text-red-700">{operationError}</p>
            <button
              onClick={() => setOperationError(null)}
              className="text-red-400 ml-3 text-xl leading-none"
              aria-label="閉じる"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </>
  );
}
